import {
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { GoChevronDown } from "react-icons/go";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import DateRangePicker from "../NoteReportModal/DateRangePicker";
import useStyles from "./style";
import { useSelector } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import { TaskReportPdf } from "../TaskReportPdf";
import { getAllTasks, getTaskReport } from "../../services/task";
import { Form8615ReportPdf } from "../Form8615Pdf";
import { decrypt } from "../../utils";
import moment from "moment";
import TaskViewReportModal from "../../pages/Home/Task/TaskViewReportModal";
import { ADL_ACTIVE_PLAN_TASK_TYPE_ID, ADL_TASK_TRIGGER_ID } from "../../pages/Home/Task/AddTask";


export  const normalizeTaskTypes = (taskTypes) => {
  if (!taskTypes) return [];

  // If it's a string, convert to array
  const typesArray = Array.isArray(taskTypes)
    ? taskTypes
    : [taskTypes];

  if (typesArray.includes(ADL_TASK_TRIGGER_ID)) {
    return Array.from(
      new Set([...typesArray, ADL_ACTIVE_PLAN_TASK_TYPE_ID])
    );
  }

  if (taskTypes === ADL_TASK_TRIGGER_ID) {
    return [taskTypes, ADL_ACTIVE_PLAN_TASK_TYPE_ID];
  }

  return typesArray;
};

const TaskReportDrawer = ({ drawerOpen, drawerClose, reportType }) => {
  const { classes, cx } = useStyles();
  const [customError, setCustomError] = useState({});
  const { allPatients } = useSelector((state) => state?.patient);
  const taskReportLoading = useSelector(
    (state) => state?.task?.taskReportLoading,
  );
  const { allFacilities } = useSelector((state) => state?.facility);
  const { staff } = useSelector((state) => state.staff);
  const { allSignature } = useSelector((state) => state?.signature);
  const { taskTypes } = useSelector((state) => state.task);
  const [taskViewModalOpened, setTaskViewModalOpened] = useState(false);

  const form = useForm({
    initialValues: {
      client: "",
      facility: "",
      taskTypes: [],
      monthValue: [], // For monthly period
      periodType: "", // 'by_date' or 'by_month'
    },
    validate: {
      client: (value) => (value ? null : "Required"),
      facility: (value) => (value ? null : "Required"),
      taskTypes: (value) =>
        reportType === "task" ? (value?.length > 0 ? null : "Required") : null,
    },
  });
  const [dateRange, setDateRange] = useState({
    type: null,
    date: [],
  });
  const [period, setPeriod] = useState([]);
  const [opened, { open, close }] = useDisclosure(false);

  const isFormValid =
    form.values.client.length > 0 &&
    form.values.facility &&
    (reportType === "task" ? form.values.taskTypes.length > 0 : true) &&
    form.values.periodType &&
    ((form.values.periodType === "by_month" &&
      form.values.monthValue.length === 2) ||
      (form.values.periodType === "by_date" && period[0] && period[1]));

  const facilityName =
    allFacilities?.facilities?.find(
      (facility) => facility.id === form.values.facility,
    )?.name || "";

  const handleApply = () => {
    if (dateRange.date?.[0] && dateRange.date?.[1]) {
      setPeriod(dateRange.date);
      setCustomError((prev) => ({ ...prev, period: "" }));
      close();
    }
  };

  const handlePopoverClose = () => {
    if (period.length === 2) {
      setDateRange({ type: "custom", date: period });
    } else {
      setDateRange({ type: null, date: [] });
    }
    close();
  };

  const handleCancel = () => {
    setPeriod([]);
    form.reset();
    setCustomError((prev) => ({ ...prev, period: "" }));
    setDateRange({
      type: null,
      date: [],
    });

    drawerClose();
  };

  const toDataURL = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn("Image load failed:", url);
      return null;
    }
  };

  const preloadAllImages = async (allSignature) => {
    const imageMap = {};
    const imageUrls = new Set();

    allSignature?.forEach((sig) => {
      ["signature", "signatureImage", "signatureInitials"].forEach((key) => {
        if (sig?.[key]) {
          try {
            const decryptedUrl = decrypt(sig[key]);
            if (decryptedUrl) imageUrls.add(decryptedUrl);
          } catch {}
        }
      });
    });

    const promises = Array.from(imageUrls).map(async (url) => {
      const base64 = await toDataURL(url);
      if (base64) imageMap[url] = base64;
    });

    await Promise.all(promises);
    return imageMap;
  };


  // const normalizeTaskTypes = (taskTypes = []) => {
  //   console.log("🚀 ~ normalizeTaskTypes ~ taskTypes:", taskTypes)
  //   if (taskTypes.includes(ADL_TASK_TRIGGER_ID)) {
  //     return Array.from(new Set([...taskTypes, ADL_ACTIVE_PLAN_TASK_TYPE_ID]));
  //   }
  //   return taskTypes;
  // };




  const handleSubmit = async (values) => {
    console.log("🚀 ~ handleSubmit ~ values:", values)
    const errors = {};

    // Check if period is selected based on periodType
    if (
      values.periodType === "by_month" &&
      (!values.monthValue || values.monthValue.length !== 2)
    ) {
      errors.period = "Please select month range";
    } else if (values.periodType === "by_date" && (!period[0] || !period[1])) {
      errors.period = "Please select date range";
    }

    if (Object.keys(errors).length) {
      setCustomError(errors);
      return;
    }

    if (values.periodType === "by_month") {
      const startOfMonth = moment(values.monthValue[0])
        .startOf("month")
        .toISOString();
      const endOfMonth = moment(values.monthValue[1])
        .endOf("month")
        .toISOString();

      // Use all selected clients
      const clientIds = values.client.length > 0 ? values.client : [];

      // Use selected task types
      const taskTypeIds = reportType === "task" ?  values.taskTypes : [];

      // Get tasks for all selected clients & task types
      getAllTasks(
        clientIds,
        taskTypeIds,
        startOfMonth,
        endOfMonth,
        values.facility,
      );

      setTaskViewModalOpened(true);
      return;
    }

    let startDate, endDate;

    // For "by_date", use daily period
    startDate = period[0];
    endDate = period[1];

    const payload = {
      facility: values.facility,
      client: values.client,
      taskTypes: values.taskTypes,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      reportType: reportType,
    };

    const imageMap = await preloadAllImages(allSignature);
    const res = await getTaskReport(payload);

    try {
      if (reportType === "task") {
        if (res) {
          const doc = (
            <TaskReportPdf
              data={res}
              dateRange={{ start: startDate, end: endDate }}
              allSignature={allSignature}
              imageMap={imageMap}
            />
          );

          const asPdf = pdf();
          asPdf.updateContainer(doc);
          const blob = await asPdf.toBlob();

          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `Task_Report_${facilityName}.pdf`;
          link.click();
          URL.revokeObjectURL(url);

          handleCancel();
        }
      } else if (reportType === "form8615") {
        const doc = (
          <Form8615ReportPdf
            data={res}
            dateRange={{ start: startDate, end: endDate }}
            allSignature={allSignature}
            imageMap={imageMap}
          />
        );
        const asPdf = pdf();
        asPdf.updateContainer(doc);
        const blob = await asPdf.toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Form8615_Report_${facilityName}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        handleCancel();
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  const handleTaskViewModalClose = () => {
    setTaskViewModalOpened(false);
  };
  // console.log('form.values.client',form.values.client);

  return (
    <Drawer
      opened={drawerOpen}
      offset={8}
      size="55%"
      sx={{
        ".mantine-Drawer-close": {
          color: "#000",
          "& svg": {
            width: "25px !important",
            height: "25px !important",
          },
        },
        ".mantine-11shxvp": {
          background: "#F5F5F5",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        ".mantine-Drawer-close:hover": {
          color: "#FFAF4A",
        },
        ".mantine-1q36a81": {
          padding: "0",
        },
      }}
      zIndex={1000}
      title={
        <Text fz={24} fw={700} ff="Inter" color="#000">
          {reportType === "form8615" ? "Form 8615" : "Task Report"}
        </Text>
      }
      position="right"
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
      shadow="md"
      onClose={handleCancel}
      centered
    >
      <Flex direction="column" py={14} px={24}>
        <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
          <Box className={classes.formBox}>
            <Grid gutter={24}>
              <Grid.Col span={6}>
                <Select
                  size="sm"
                  className={classes.inputField}
                  searchable
                  withAsterisk
                  label="Facility"
                  placeholder="Select Facility"
                  rightSectionPointerEvents="none"
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  {...form.getInputProps("facility")}
                  onChange={(val) => {
                    form.setFieldValue("client", "");
                    form.setFieldValue("facility", val);
                  }}
                  data={
                    allFacilities && allFacilities?.facilities
                      ? allFacilities?.facilities
                          ?.filter(({ active, deleted }) => active && !deleted)
                          ?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                      : []
                  }
                />
              </Grid.Col>
              {reportType !== "task" && (
                <Grid.Col span={6}>
                  <MultiSelect
                    searchable
                    label="Client"
                    disabled={!form.values.facility}
                    multiple
                    withAsterisk
                    size="sm"
                    className={cx(classes.inputField, classes.multiSelect)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    placeholder="Select Client"
                    rightSectionPointerEvents="none"
                    data={[
                      { value: "ALL", label: "All Clients" },
                      ...(allPatients
                        ? allPatients
                            ?.filter(
                              (p) =>
                                p?.active === true &&
                                p?.deleted === false &&
                                p?.facilityId?.some(
                                  (f) =>
                                    f?.id === form.values.facility &&
                                    f?.active === true &&
                                    f?.deleted === false,
                                ),
                            )
                            ?.map((opt) => ({
                              value: opt?.id,
                              label: opt?.name,
                            }))
                        : []),
                    ]}
                    value={form.values.client}
                    onChange={(val) => {
                      if (val.includes("ALL")) {
                        const facilityClients = allPatients
                          ?.filter(
                            (p) =>
                              p?.active === true &&
                              p?.deleted === false &&
                              p?.facilityId?.some(
                                (f) =>
                                  f?.id === form.values.facility &&
                                  f?.active === true &&
                                  f?.deleted === false,
                              ),
                          )
                          ?.map((opt) => opt?.id);

                        form.setFieldValue("client", facilityClients);
                      } else {
                        form.setFieldValue("client", val);
                      }
                    }}
                    error={form.errors.client}
                  />
                </Grid.Col>
              )}

              <Grid.Col span={6}>
                <Select
                  size="sm"
                  className={classes.inputField}
                  withAsterisk
                  label="Period Type"
                  placeholder="Select Period Type"
                  rightSectionPointerEvents="none"
                  rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                  {...form.getInputProps("periodType")}
                  onChange={(val) => {
                    form.setFieldValue("periodType", val);
                    // Clear both periods when switching type
                    form.setFieldValue("monthValue", []);
                    setPeriod([]);
                    setDateRange({ type: null, date: [] });
                    setCustomError((prev) => ({ ...prev, period: "" }));
                  }}
                  data={[
                    { value: "by_month", label: "By Month" },
                    { value: "by_date", label: "By Date" },
                  ]}
                />
              </Grid.Col>

              {form.values.periodType === "by_month" && (
                <Grid.Col span={6}>
                  <Box style={{ position: "relative" }}>
                    <MonthPickerInput
                      size="sm"
                      type="range"
                      allowSingleDateInRange
                      label="Select Months"
                      placeholder="Select Months"
                      {...form.getInputProps("monthValue")}
                      onChange={(val) => {
                        form.setFieldValue("monthValue", val);
                        setCustomError((prev) => ({ ...prev, period: "" }));
                      }}
                      styles={{
                        input: {
                          paddingRight:
                            form.values.monthValue?.length > 0
                              ? "35px"
                              : "12px",
                        },
                      }}
                    />
                    {form.values.monthValue?.length > 0 && (
                      <Box
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          cursor: "pointer",
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                        }}
                        onClick={() => {
                          form.setFieldValue("monthValue", []);
                          setCustomError((prev) => ({ ...prev, period: "" }));
                        }}
                      >
                        <Text fz={18} fw={700} color="#94A3B8">
                          ×
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Grid.Col>
              )}

              {form.values.periodType === "by_date" && (
                <Grid.Col span={6}>
                  <Box style={{ position: "relative" }}>
                    <Text fz={14} fw={500} ff="Inter" color="#262626">
                      Select Date Range
                    </Text>

                    <DateRangePicker
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      handleApply={() => {
                        handleApply();
                      }}
                      open={open}
                      opened={opened}
                      close={handlePopoverClose}
                      size="md"
                      mt={20}
                      style={{ marginTop: "20px" }}
                    />
                    {period.length === 2 && (
                      <Box
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          cursor: "pointer",
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 20,
                        }}
                        onClick={() => {
                          setPeriod([]);
                          setDateRange({ type: null, date: [] });
                          setCustomError((prev) => ({ ...prev, period: "" }));
                        }}
                      >
                        <Text fz={18} fw={700} color="#94A3B8">
                          ×
                        </Text>
                      </Box>
                    )}
                    {customError.period && (
                      <Text color="red" fz={16} mt={4}>
                        {customError.period}
                      </Text>
                    )}
                  </Box>
                </Grid.Col>
              )}

              {reportType === "task" && (
                <>
                  <Grid.Col span={6}>
                    {form.values.periodType === "by_month" ? (
                      /* ---------- SINGLE SELECT ---------- */
                      <Select
                        searchable
                        label="Client"
                        disabled={
                          !form.values.facility || !form.values.periodType
                        }
                        withAsterisk
                        size="sm"
                        className={classes.inputField}
                        rightSection={
                          <GoChevronDown color="#94A3B8" size={16} />
                        }
                        placeholder="Select Client"
                        data={
                          allPatients
                            ? allPatients
                                .filter(
                                  (p) =>
                                    p?.active === true &&
                                    p?.deleted === false &&
                                    p?.facilityId?.some(
                                      (f) =>
                                        f?.id === form.values.facility &&
                                        f?.active === true &&
                                        f?.deleted === false,
                                    ),
                                )
                                .map((opt) => ({
                                  value: opt?.id,
                                  label: opt?.name,
                                }))
                            : []
                        }
                        value={form.values.client || null}
                        onChange={(val) => {
                          form.setFieldValue("client", val);
                        }}
                        error={form.errors.client}
                      />
                    ) : (
                      /* ---------- MULTI SELECT ---------- */
                      <MultiSelect
                        searchable
                        label="Client"
                        disabled={
                          !form.values.facility || !form.values.periodType
                        }
                        multiple
                        withAsterisk
                        size="sm"
                        className={cx(classes.inputField, classes.multiSelect)}
                        rightSection={
                          <GoChevronDown color="#94A3B8" size={16} />
                        }
                        placeholder="Select Client"
                        rightSectionPointerEvents="none"
                        data={[
                          { value: "ALL", label: "All Clients" },
                          ...(allPatients
                            ? allPatients
                                .filter(
                                  (p) =>
                                    p?.active === true &&
                                    p?.deleted === false &&
                                    p?.facilityId?.some(
                                      (f) =>
                                        f?.id === form.values.facility &&
                                        f?.active === true &&
                                        f?.deleted === false,
                                    ),
                                )
                                .map((opt) => ({
                                  value: opt?.id,
                                  label: opt?.name,
                                }))
                            : []),
                        ]}
                        value={form.values.client || []}
                        onChange={(val) => {
                          if (val.includes("ALL")) {
                            const facilityClients = allPatients
                              ?.filter(
                                (p) =>
                                  p?.active === true &&
                                  p?.deleted === false &&
                                  p?.facilityId?.some(
                                    (f) =>
                                      f?.id === form.values.facility &&
                                      f?.active === true &&
                                      f?.deleted === false,
                                  ),
                              )
                              ?.map((opt) => opt?.id);

                            form.setFieldValue("client", facilityClients);
                          } else {
                            form.setFieldValue("client", val);
                          }
                        }}
                        error={form.errors.client}
                      />
                    )}
                  </Grid.Col>

                  <Grid.Col span={6}>
                    {form.values.periodType === "by_month" ? (
                      /* ---------- SINGLE SELECT ---------- */
                      <Select
                        searchable
                        label="Task Type"
                        withAsterisk
                        size="sm"
                        className={classes.inputField}
                        rightSection={
                          <GoChevronDown color="#94A3B8" size={16} />
                        }
                        placeholder="Select Task Type"
                        data={
                          taskTypes
                            ? taskTypes.map((opt) => ({
                                value: opt?.id,
                                label: opt?.name,
                              }))
                            : []
                        }
                        value={form.values.taskTypes || null}
                        onChange={(val) => {
                          form.setFieldValue("taskTypes", val);
                        }}
                        error={form.errors.taskTypes}
                      />
                    ) : (
                      /* ---------- MULTI SELECT ---------- */
                      <MultiSelect
                        searchable
                        label="Task Type"
                        multiple
                        withAsterisk
                        size="sm"
                        className={cx(classes.inputField, classes.multiSelect)}
                        rightSection={
                          <GoChevronDown color="#94A3B8" size={16} />
                        }
                        placeholder="Select Task Type"
                        rightSectionPointerEvents="none"
                        data={[
                          { value: "ALL", label: "All Task Types" },
                          ...(taskTypes
                            ? taskTypes.map((opt) => ({
                                value: opt?.id,
                                label: opt?.name,
                              }))
                            : []),
                        ]}
                        value={form.values.taskTypes || []}
                        onChange={(val) => {
                          if (val.includes("ALL")) {
                            const allTypes =
                              taskTypes?.map((opt) => opt?.id) || [];
                            form.setFieldValue("taskTypes", allTypes);
                          } else {
                            form.setFieldValue("taskTypes", val);
                          }
                        }}
                        error={form.errors.taskTypes}
                      />
                    )}
                  </Grid.Col>
                </>
              )}

              {/* {reportType === "task" && (
                <Grid.Col span={6}>
                  <MultiSelect
                    searchable
                    label="Task Type"
                    multiple
                    withAsterisk
                    size="sm"
                    className={cx(classes.inputField, classes.multiSelect)}
                    rightSection={<GoChevronDown color="#94A3B8" size={16} />}
                    placeholder="Select Task Type"
                    rightSectionPointerEvents="none"
                    data={[
                      { value: "ALL", label: "All Task Types" },
                      ...(taskTypes
                        ? taskTypes?.map((opt) => ({
                            value: opt?.id,
                            label: opt?.name,
                          }))
                        : []),
                    ]}
                    value={form.values.taskTypes}
                    onChange={(val) => {
                      if (val.includes("ALL")) {
                        const allTypes = taskTypes?.map((opt) => opt?.id) || [];
                        form.setFieldValue("taskTypes", allTypes);
                      } else {
                        form.setFieldValue("taskTypes", val);
                      }
                    }}
                    error={form.errors.taskTypes}
                  />
                </Grid.Col>
              )} */}
            </Grid>
          </Box>
          <Flex gap={20} align="center" justify="flex-end">
            <Button
              className={classes.cancelBtn}
              onClick={() => handleCancel()}
            >
              Cancel
            </Button>
            <Button
              disabled={!isFormValid}
              type="submit"
              className={classes.createBtn}
              loading={taskReportLoading}
            >
              Create report
            </Button>
          </Flex>
        </form>
      </Flex>
      {/* Add TaskViewReportModal */}
      <TaskViewReportModal
        monthValue={form.values.monthValue}
        selectedClient={allPatients?.find(
          (p) =>
            p.id ===
            (form.values.periodType === "by_month"
              ? form.values.client
              : form.values.client?.[0]),
        )}
        // selectedClient={allPatients?.find(p => p.id === form.values.client[0])}
        // selectedClient={allPatients?.find(p => p.id === form.values.client)}
        opened={taskViewModalOpened}
        onClose={handleTaskViewModalClose}
      />
    </Drawer>
  );
};

export default TaskReportDrawer;

// import {
//   Box,
//   Button,
//   Drawer,
//   Flex,
//   Grid,
//   MultiSelect,
//   Select,
//   Text,
// } from "@mantine/core";
// import { GoChevronDown } from "react-icons/go";
// import { useForm } from "@mantine/form";
// import { useDisclosure } from "@mantine/hooks";
// import { useState } from "react";
// import DateRangePicker from "../NoteReportModal/DateRangePicker";
// import useStyles from "./style";
// import { useSelector } from "react-redux";
// import { pdf } from "@react-pdf/renderer";
// import { TaskReportPdf } from "../TaskReportPdf";
// import { getTaskReport } from "../../services/task";
// import { Form8615ReportPdf } from "../Form8615Pdf";
// import { decrypt } from "../../utils";

// const TaskReportDrawer = ({ drawerOpen, drawerClose, reportType }) => {
//   const { classes, cx } = useStyles();
//   const [customError, setCustomError] = useState({});
//   const { allPatients } = useSelector((state) => state?.patient);
//   const taskReportLoading = useSelector(
//     (state) => state?.task?.taskReportLoading
//   );
//   const { allFacilities } = useSelector((state) => state?.facility);
//   const { staff } = useSelector((state) => state.staff);
//   const { allSignature } = useSelector((state) => state?.signature);
//   const { taskTypes } = useSelector((state) => state.task);

//   const form = useForm({
//     initialValues: {
//       client: "",
//       facility: "",
//       taskTypes: [],
//     },
//     validate: {
//       client: (value) => (value ? null : "Required"),
//       facility: (value) => (value ? null : "Required"),
//       taskTypes: (value) =>
//         reportType === "task" ? (value?.length > 0 ? null : "Required") : null,
//     },
//   });
//   const [dateRange, setDateRange] = useState({
//     type: null,
//     date: [],
//   });
//   const [period, setPeriod] = useState([]);
//   const [opened, { open, close }] = useDisclosure(false);

//   const isFormValid =
//     form.values.client.length > 0 &&
//     form.values.facility &&
//     (reportType === "task" ? form.values.taskTypes.length > 0 : true) &&
//     period[0] &&
//     period[1];

//   const facilityName =
//     allFacilities?.facilities?.find(
//       (facility) => facility.id === form.values.facility
//     )?.name || "";
//   const handleApply = () => {
//     if (dateRange.date?.[0] && dateRange.date?.[1]) {
//       setPeriod(dateRange.date);
//       setCustomError((prev) => ({ ...prev, period: "" }));
//       close();
//     }
//   };
//   const handleCancel = () => {
//     setPeriod([]);
//     form.reset();
//     setCustomError((prev) => ({ ...prev, period: "" }));
//     setDateRange({
//       type: null,
//       date: [],
//     });

//     drawerClose();
//   };

//   const toDataURL = async (url) => {
//     try {
//       const res = await fetch(url, { mode: "cors" });
//       const blob = await res.blob();
//       return new Promise((resolve, reject) => {
//         const reader = new FileReader();
//         reader.onloadend = () => resolve(reader.result);
//         reader.onerror = reject;
//         reader.readAsDataURL(blob);
//       });
//     } catch (err) {
//       console.warn("Image load failed:", url);
//       return null;
//     }
//   };

//   const preloadAllImages = async (allSignature) => {
//     const imageMap = {}; // store url → base64
//     const imageUrls = new Set();

//     allSignature?.forEach((sig) => {
//       ["signature", "signatureImage", "signatureInitials"].forEach((key) => {
//         if (sig?.[key]) {
//           try {
//             const decryptedUrl = decrypt(sig[key]);
//             if (decryptedUrl) imageUrls.add(decryptedUrl);
//           } catch {}
//         }
//       });
//     });

//     // Convert all URLs to Base64
//     const promises = Array.from(imageUrls).map(async (url) => {
//       const base64 = await toDataURL(url);
//       if (base64) imageMap[url] = base64;
//     });

//     await Promise.all(promises);
//     return imageMap;
//   };

//   const handleSubmit = async (values) => {
//     const errors = {};
//     if (!period[0] || !period[1]) {
//       errors.period = "Required";
//     }

//     if (Object.keys(errors).length) {
//       setCustomError(errors);
//       return;
//     }
//     const payload = {
//       facility: values.facility,
//       client: values.client,
//       taskTypes: values.taskTypes,
//       startDate: period[0].toISOString(),
//       endDate: period[1].toISOString(),
//       reportType: reportType,
//     };
//     const imageMap = await preloadAllImages(allSignature);
//     const res = await getTaskReport(payload);
//     try {
//       if (reportType === "task") {
//         if (res) {
//           const doc = (
//             <TaskReportPdf
//               data={res}
//               dateRange={{ start: period[0], end: period[1] }}
//               allSignature={allSignature}
//               imageMap={imageMap}
//             />
//           );

//           const asPdf = pdf();
//           asPdf.updateContainer(doc);
//           const blob = await asPdf.toBlob();

//           const url = URL.createObjectURL(blob);
//           const link = document.createElement("a");
//           link.href = url;
//           link.download = `Task_Report_${facilityName}.pdf`;
//           link.click();
//           URL.revokeObjectURL(url);

//           handleCancel();
//         }
//       } else if (reportType === "form8615") {
//         const doc = (
//           <Form8615ReportPdf
//             data={res}
//             dateRange={{ start: period[0], end: period[1] }}
//             allSignature={allSignature}
//             imageMap={imageMap}
//           />
//         );
//         const asPdf = pdf();
//         asPdf.updateContainer(doc);
//         const blob = await asPdf.toBlob();

//         const url = URL.createObjectURL(blob);
//         const link = document.createElement("a");
//         link.href = url;
//         link.download = `Form8615_Report_${facilityName}.pdf`;
//         link.click();
//         URL.revokeObjectURL(url);
//         handleCancel();
//       }
//     } catch (error) {
//       console.log("error:", error);
//     }
//   };

//   return (
//     <Drawer
//       opened={drawerOpen}
//       offset={8}
//       size="55%"
//       sx={{
//         ".mantine-Drawer-close": {
//           color: "#000",
//           "& svg": {
//             width: "25px !important",
//             height: "25px !important",
//           },
//         },
//         ".mantine-11shxvp": {
//           background: "#F5F5F5",
//           borderTopLeftRadius: 16,
//           borderBottomLeftRadius: 16,
//         },
//         ".mantine-Drawer-close:hover": {
//           color: "#FFAF4A",
//         },
//         ".mantine-1q36a81": {
//           padding: "0",
//         },
//       }}
//       zIndex={1000}
//       title={
//         <Text fz={24} fw={700} ff="Inter" color="#000">
//           {reportType === "form8615" ? "Form 8615" : "Task Report"}
//         </Text>
//       }
//       position="right"
//       overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
//       shadow="md"
//       onClose={handleCancel}
//       centered
//     >
//       <Flex direction="column" py={14} px={24}>
//         <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
//           <Box className={classes.formBox}>
//             <Grid gutter={24}>
//               <Grid.Col span={6}>
//                 <Select
//                   size="sm"
//                   className={classes.inputField}
//                   searchable
//                   withAsterisk
//                   label="Facility"
//                   placeholder="Select Facility"
//                   rightSectionPointerEvents="none"
//                   rightSection={<GoChevronDown color="#94A3B8" size={16} />}
//                   {...form.getInputProps("facility")}
//                   onChange={(val) => {
//                     form.setFieldValue("client", "");
//                     form.setFieldValue("facility", val);
//                   }}
//                   data={
//                     allFacilities && allFacilities?.facilities
//                       ? allFacilities?.facilities
//                           ?.filter(({ active, deleted }) => active && !deleted)
//                           ?.map((opt) => ({
//                             value: opt?.id,
//                             label: opt?.name,
//                           }))
//                       : []
//                   }
//                 />
//               </Grid.Col>
//               <Grid.Col span={6}>
//                 <MultiSelect
//                   searchable
//                   label="Client"
//                   disabled={!form.values.facility}
//                   multiple
//                   withAsterisk
//                   size="sm"
//                   className={cx(classes.inputField, classes.multiSelect)}
//                   rightSection={<GoChevronDown color="#94A3B8" size={16} />}
//                   placeholder="Select Client"
//                   rightSectionPointerEvents="none"
//                   data={[
//                     { value: "ALL", label: "All Clients" }, // 👈 Add this at the top
//                     ...(allPatients
//                       ? allPatients
//                           ?.filter(
//                             (p) =>
//                               p?.active === true &&
//                               p?.deleted === false &&
//                               p?.facilityId?.some(
//                                 (f) =>
//                                   f?.id === form.values.facility &&
//                                   f?.active === true &&
//                                   f?.deleted === false
//                               )
//                           )
//                           ?.map((opt) => ({
//                             value: opt?.id,
//                             label: opt?.name,
//                           }))
//                       : []),
//                   ]}
//                   value={form.values.client}
//                   onChange={(val) => {
//                     if (val.includes("ALL")) {
//                       // get all clients for this facility
//                       const facilityClients = allPatients
//                         ?.filter(
//                           (p) =>
//                             p?.active === true &&
//                             p?.deleted === false &&
//                             p?.facilityId?.some(
//                               (f) =>
//                                 f?.id === form.values.facility &&
//                                 f?.active === true &&
//                                 f?.deleted === false
//                             )
//                         )
//                         ?.map((opt) => opt?.id);

//                       // set all client ids
//                       form.setFieldValue("client", facilityClients);
//                     } else {
//                       form.setFieldValue("client", val);
//                     }
//                   }}
//                   error={form.errors.client}
//                 />
//               </Grid.Col>
//               {reportType === "task" && (
//                 <Grid.Col span={6}>
//                   <MultiSelect
//                     searchable
//                     label="Task Type"
//                     multiple
//                     withAsterisk
//                     size="sm"
//                     className={cx(classes.inputField, classes.multiSelect)}
//                     rightSection={<GoChevronDown color="#94A3B8" size={16} />}
//                     placeholder="Select Task Type"
//                     rightSectionPointerEvents="none"
//                     data={[
//                       { value: "ALL", label: "All Task Types" },
//                       ...(taskTypes
//                         ? taskTypes?.map((opt) => ({
//                             value: opt?.id,
//                             label: opt?.name,
//                           }))
//                         : []),
//                     ]}
//                     value={form.values.taskTypes}
//                     onChange={(val) => {
//                       if (val.includes("ALL")) {
//                         const allTypes = taskTypes?.map((opt) => opt?.id) || [];
//                         form.setFieldValue("taskTypes", allTypes);
//                       } else {
//                         form.setFieldValue("taskTypes", val);
//                       }
//                     }}
//                     error={form.errors.taskTypes}
//                   />
//                 </Grid.Col>
//               )}
//               <Grid.Col span={6}>
//                 <Text fz={14} fw={500} ff="Inter" color="#262626">
//                   Period <span style={{ color: "red" }}>*</span>
//                 </Text>

//                 <DateRangePicker
//                   dateRange={dateRange}
//                   setDateRange={setDateRange}
//                   handleApply={handleApply}
//                   open={open}
//                   opened={opened}
//                   close={close}
//                   size="md"
//                   mt={20}
//                   style={{ marginTop: "20px" }}
//                 />
//                 {customError.period && (
//                   <Text color="red" fz={16} mt={4}>
//                     {customError.period}
//                   </Text>
//                 )}
//               </Grid.Col>
//             </Grid>
//           </Box>
//           <Flex gap={20} align="center" justify="flex-end">
//             <Button
//               className={classes.cancelBtn}
//               onClick={() => handleCancel()}
//             >
//               Cancel
//             </Button>
//             <Button
//               disabled={!isFormValid}
//               type="submit"
//               className={classes.createBtn}
//               loading={taskReportLoading}
//             >
//               Create report
//             </Button>
//           </Flex>
//         </form>
//       </Flex>
//     </Drawer>
//   );
// };

// export default TaskReportDrawer;
